import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import ChartWidgetWrapper from '../ChartWidgetWrapper';
import HeatmapChart from './HeatmapChart';
import { useFetchTableData } from '../../../customDashboards/hooks';
import { getMetricsFormat, getMetricValueFormat } from '../PivotChart/utils';
import { getReportTypeFromDataType, normalizeDimensionNames } from '../../../customDashboards/utils';
import { calculatedMetricValue } from '../../../utils/grids';
import { ACTION_TYPES } from '../ChartWidgetWrapper/consts';
import { buildHeatmapSeries } from '../../../customDashboards/heatmap/heatmapData';
import { buildColorAxis, shouldShowValues } from '../../../customDashboards/heatmap/heatmapOptions';
import { getBlockedWidgetFilterNames } from '../../../customDashboards/universalFiltersConfig';
import { OPERATOR_TYPES } from '../../../utils/dashboardConsts';

const HeatmapChartWidget = ({
    dispatchWidgetVersions,
    dispatchWidgetsLoading,
    forceRefreshCounter,
    dashboardId,
    widget,
    globalFilters = [],
    startDate,
    endDate,
    startDate2 = null,
    endDate2 = null,
    compare,
    height,
    width,
    updatingGraphQL = false,
    allowedActions = [],
    onMenuClick = () => {},
    onCloneClick = () => {},
    onDeleteClick = () => {},
    displayUnenriched = false,
    apiFields = null,
    readOnly = false,
}) => {
    const reportType = getReportTypeFromDataType(widget.dataType, widget.type);
    const chartRef = useRef(null);

    const [loading, tableData, error] = useFetchTableData(
        dashboardId,
        widget.gridParams.i,
        widget.query,
        globalFilters,
        startDate,
        endDate,
        startDate2,
        endDate2,
        reportType,
        dispatchWidgetVersions,
        dispatchWidgetsLoading,
        compare,
        widget.dataType,
        displayUnenriched,
        updatingGraphQL,
        forceRefreshCounter,
        undefined,
        widget.type,
        widget.options
    );

    const { rowData = [] } = tableData || {};

    const reportColumns = useMemo(
        () =>
            (tableData?.columnDefs || []).map(x => ({
                name: x.field,
                fieldType: x.type,
                displayName: x.headerName,
                displayFormat: x.context?.displayFormat,
            })),
        [tableData]
    );

    const { xCategories, yCategories, data, valueFormat, xTitle, yTitle } = useMemo(() => {
        const resolveField = fieldName =>
            normalizeDimensionNames([fieldName], widget.dataType, widget.query.skanDateDimensionName).find(name =>
                reportColumns.find(c => c.name === name)
            );

        const getRawValue = (key, row) => {
            if (!key) return undefined;
            const value = typeof row[key] === 'object' ? calculatedMetricValue(row[key]) : row[key];
            const col = reportColumns.find(c => c.name === key);
            if (col?.displayFormat?.type === 'percentage' && typeof value === 'number') {
                return value * 100;
            }
            return value;
        };

        if (!reportColumns.length) {
            return { xCategories: [], yCategories: [], data: [], valueFormat: null, xTitle: '', yTitle: '' };
        }
        if (!widget.options.rows?.[0] || !widget.options.columns?.[0] || !widget.query.metrics?.[0]) {
            return { xCategories: [], yCategories: [], data: [], valueFormat: null, xTitle: '', yTitle: '' };
        }
        const xField = resolveField(widget.options.rows[0]);
        const yField = resolveField(widget.options.columns[0]);
        const valueField = resolveField(widget.query.metrics[0]);

        const points = rowData.map(row => ({
            x: getRawValue(xField, row),
            y: getRawValue(yField, row),
            value: getRawValue(valueField, row),
        }));

        const series = buildHeatmapSeries(points);

        const metricsFmt = getMetricsFormat(reportColumns);
        const valueMetric = metricsFmt.find(m => m.name === valueField);
        const fmt = valueMetric ? getMetricValueFormat(valueMetric) : null;

        return {
            xCategories: series.xCategories,
            yCategories: series.yCategories,
            data: series.data,
            valueFormat: fmt
                ? {
                      prefix: fmt.valuePrefix,
                      suffix: fmt.valueSuffix,
                      precision: fmt.valueDecimals,
                      displayName: valueMetric.display_name,
                  }
                : null,
            xTitle: reportColumns.find(c => c.name === xField)?.displayName || '',
            yTitle: reportColumns.find(c => c.name === yField)?.displayName || '',
        };
    }, [reportColumns, rowData, widget.dataType, widget.options, widget.query]);

    const isDataParsed = data.length > 0;
    const colorAxis = useMemo(() => buildColorAxis(widget.heatMapChartOptions), [widget.heatMapChartOptions]);

    const handleExportClick = () => {
        const chart = chartRef.current?.chart;
        if (!chart) return;
        const exportFn = chart.exportChartLocal ?? chart.exportChart;
        if (typeof exportFn === 'function') {
            exportFn.call(chart, { filename: widget.name || 'heatmap' }, { title: { text: widget.name } });
        }
    };

    return (
        <ChartWidgetWrapper
            apiFields={apiFields}
            widgetTitle={widget.name}
            widgetType={widget.type}
            widgetDataType={widget.dataType}
            widgetFilters={widget.query.filters}
            showSpinner={loading}
            showNoData={!loading && !error && !isDataParsed}
            blockedFilters={getBlockedWidgetFilterNames(widget.dataType, globalFilters)}
            showError={!loading && !!error}
            allowedActions={allowedActions}
            onMenuClick={onMenuClick}
            onCloneClick={onCloneClick}
            onDeleteClick={onDeleteClick}
            onExportClick={handleExportClick}
            readOnly={readOnly}
        >
            {!loading && !error && isDataParsed && (
                <HeatmapChart
                    xCategories={xCategories}
                    yCategories={yCategories}
                    data={data}
                    colorAxis={colorAxis}
                    defaultColor={widget.heatMapChartOptions?.defaultColor}
                    showValues={shouldShowValues(widget.heatMapChartOptions)}
                    xAxisTitle={xTitle}
                    yAxisTitle={yTitle}
                    valueFormat={valueFormat}
                    height={height}
                    width={width}
                    chartRef={chartRef}
                />
            )}
        </ChartWidgetWrapper>
    );
};

HeatmapChartWidget.propTypes = {
    dispatchWidgetVersions: PropTypes.func.isRequired,
    dispatchWidgetsLoading: PropTypes.func.isRequired,
    forceRefreshCounter: PropTypes.number,
    dashboardId: PropTypes.string.isRequired,
    widget: PropTypes.shape({
        name: PropTypes.string.isRequired,
        dataType: PropTypes.string.isRequired,
        type: PropTypes.oneOf(['heatmap']).isRequired,
        query: PropTypes.shape({
            dimensions: PropTypes.arrayOf(PropTypes.string),
            metrics: PropTypes.arrayOf(PropTypes.string),
            skanDateDimensionName: PropTypes.string,
            filters: PropTypes.arrayOf(
                PropTypes.shape({
                    field: PropTypes.string.isRequired,
                    operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
                    values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
                })
            ),
        }).isRequired,
        options: PropTypes.shape({
            rows: PropTypes.arrayOf(PropTypes.string),
            columns: PropTypes.arrayOf(PropTypes.string),
        }).isRequired,
        gridParams: PropTypes.shape({
            i: PropTypes.string,
        }).isRequired,
        heatMapChartOptions: PropTypes.shape({
            mode: PropTypes.string,
            stops: PropTypes.arrayOf(
                PropTypes.shape({
                    stop: PropTypes.number,
                    color: PropTypes.string,
                })
            ),
            dataClasses: PropTypes.arrayOf(
                PropTypes.shape({
                    from: PropTypes.number,
                    to: PropTypes.number,
                    color: PropTypes.string,
                    name: PropTypes.string,
                })
            ),
            defaultColor: PropTypes.string,
            showValues: PropTypes.bool,
        }),
    }).isRequired,
    globalFilters: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string.isRequired,
            operator: PropTypes.oneOf(OPERATOR_TYPES.map(x => x.name)).isRequired,
            values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
        })
    ),
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    startDate2: PropTypes.string,
    endDate2: PropTypes.string,
    compare: PropTypes.bool.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    updatingGraphQL: PropTypes.bool,
    allowedActions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(ACTION_TYPES))),
    onMenuClick: PropTypes.func,
    onCloneClick: PropTypes.func,
    onDeleteClick: PropTypes.func,
    displayUnenriched: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types -- matches ChartWidgetWrapper/DashboardGrid apiFields convention
    apiFields: PropTypes.objectOf(PropTypes.any),
    readOnly: PropTypes.bool,
};

export default HeatmapChartWidget;
